import { useCallback, useEffect, useState } from "react";
import { getBranchesByOrganizationAsync } from "../api/BranchApi";
import { Branch } from "../models/BranchModels";

interface UseBranchesReturn {
  branches: Branch[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addLocal: (branch: Branch) => void;
  updateLocal: (branch: Branch) => void;
  removeLocal: (id: string) => void;
}

export const useBranches = (
  organizationId: string | undefined,
): UseBranchesReturn => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setBranches([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getBranchesByOrganizationAsync(organizationId);
      if (res.success) {
        setBranches(res.result ?? []);
        setError(null);
      } else {
        setError(res.message || "Failed to load branches");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load branches");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  const addLocal = useCallback((branch: Branch) => {
    setBranches((prev) => [branch, ...prev]);
  }, []);

  const updateLocal = useCallback((branch: Branch) => {
    setBranches((prev) =>
      prev.map((item) => (item.id === branch.id ? branch : item)),
    );
  }, []);

  const removeLocal = useCallback((id: string) => {
    setBranches((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return {
    branches,
    loading,
    error,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  };
};
